import { MessageRole } from '@/types';

import { api } from './index';

import type {
  MessageThread,
  MessageThreadMessage,
  PaginatedResponse,
} from '@/types';

export interface MessageThreadApiOptions {
  limit?: number;
  page?: number;
  userId?: string;
}

export interface GetMessageThreadMessagesParams {
  limit?: number;
  messageThreadId: string;
  page?: number;
}

export interface CreateMessageThreadParams extends Partial<MessageThread> {
  workflow?: string;
}

export interface CreateMessageThreadMessageParams {
  content: string;
  messageThreadId: string;
}

export interface UpdateMessageThreadMessageParams {
  content: string;
  messageId: string;
}

export interface ConfirmMessageThreadMessageParams {
  confirmed: boolean;
  content: string;
  messageId: string;
}

export interface DeleteMessageThreadParams {
  messageThreadId: string;
}

export const messageThreadApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get message threads grouped by date/status
    getMessageThreads: builder.query<
      PaginatedResponse<Record<string, MessageThread[]>>,
      MessageThreadApiOptions | undefined
    >({
      providesTags: ['MessageThread'],
      query: (params) => ({
        method: 'GET',
        url: '/message_thread',
        params,
      }),
    }),

    // Get messages for a specific thread with infinite scroll support
    getMessageThreadMessages: builder.query<
      PaginatedResponse<MessageThreadMessage>,
      GetMessageThreadMessagesParams
    >({
      providesTags: (_, __, { messageThreadId }) => [
        { type: 'MessageThreadMessage', id: messageThreadId },
        'MessageThreadMessage',
      ],
      query: (params) => ({
        method: 'GET',
        url: '/messages',
        params,
      }),
    }),

    // Create a new message thread
    createMessageThread: builder.mutation<
      MessageThread,
      CreateMessageThreadParams
    >({
      invalidatesTags: ['MessageThread'],
      query: (messageThread) => ({
        method: 'POST',
        url: '/message_thread',
        data: messageThread,
      }),
    }),

    // Delete a message thread
    deleteMessageThread: builder.mutation<void, DeleteMessageThreadParams>({
      invalidatesTags: ['MessageThread'],
      query: ({ messageThreadId }) => ({
        method: 'DELETE',
        url: `/message_thread/${messageThreadId}`,
      }),
    }),

    // Create a new message in a thread
    createMessageThreadMessage: builder.mutation<
      MessageThreadMessage,
      CreateMessageThreadMessageParams
    >({
      invalidatesTags: (_, __, { messageThreadId }) => [
        { type: 'MessageThreadMessage', id: messageThreadId },
        'MessageThreadMessage',
      ],
      onQueryStarted: async (
        { content, messageThreadId },
        { dispatch, queryFulfilled },
      ) => {
        // Optimistically add the message to the cache
        const patchResult = dispatch(
          messageThreadApi.util.updateQueryData(
            'getMessageThreadMessages',
            { messageThreadId },
            (draft) => {
              // Create optimistic message
              const optimisticMessage: MessageThreadMessage = {
                id: `temp-${Date.now()}`,
                content,
                messageThreadId,
                role: MessageRole.USER,
                sortKey: Date.now() / 1000,
                extendable: {},
              };

              // Add to the end of the items array
              draft.items.push(optimisticMessage);
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          // Revert optimistic update on error
          patchResult.undo();
        }
      },
      query: (params) => ({
        method: 'POST',
        url: '/messages',
        data: { message: params },
      }),
    }),

    // Update a message
    updateMessageThreadMessage: builder.mutation<
      MessageThreadMessage,
      UpdateMessageThreadMessageParams
    >({
      invalidatesTags: ['MessageThreadMessage'],
      query: ({ messageId, ...data }) => ({
        method: 'PUT',
        url: `/messages/${messageId}`,
        data: { message: data },
      }),
    }),

    // Confirm a message
    confirmMessageThreadMessage: builder.mutation<
      Record<string, never>,
      ConfirmMessageThreadMessageParams
    >({
      invalidatesTags: ['MessageThreadMessage'],
      query: ({ messageId, ...data }) => ({
        method: 'POST',
        url: `/messages/${messageId}/confirm`,
        data,
      }),
    }),

    // Delete a message
    deleteMessageThreadMessage: builder.mutation<void, { messageId: string }>({
      invalidatesTags: ['MessageThreadMessage'],
      query: ({ messageId }) => ({
        method: 'DELETE',
        url: `/messages/${messageId}`,
      }),
    }),
  }),
});

export const {
  useGetMessageThreadsQuery,
  useGetMessageThreadMessagesQuery,
  useLazyGetMessageThreadMessagesQuery,
  useCreateMessageThreadMutation,
  useDeleteMessageThreadMutation,
  useCreateMessageThreadMessageMutation,
  useUpdateMessageThreadMessageMutation,
  useConfirmMessageThreadMessageMutation,
  useDeleteMessageThreadMessageMutation,
} = messageThreadApi;
